import React, { PropsWithChildren, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ApiIcon from '@material-ui/icons/Code';
import PeopleIcon from '@material-ui/icons/People';
import {
  Sidebar,
  sidebarConfig,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
  Progress,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { useEpDevPAppContext } from '@solace-labs/backstage-plugin-ep-devp';
import { EpDevPHelpSidebarItem } from '../Help/EpDevPHelpSidebarItem';
import {
  EpDevPPluginIds,
} from '@solace-labs/backstage-plugin-ep-devp-common';
import {
  EpDevPConsumerProductsIcon,
  EpDevPConsumerAppsIcon,
  EpDevPManageApps4ApprovalsIcon,
} from '@solace-labs/backstage-plugin-ep-devp-react';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import VisibilityIcon from '@material-ui/icons/Visibility';
import QueueIcon from '@material-ui/icons/Queue';
import NotificationsIcon from '@material-ui/icons/Notifications';
import {
  useApi,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import AddAlertIcon from '@material-ui/icons/AddAlert';
import ImportContactsIcon from '@material-ui/icons/ImportContacts';
import AssignmentIcon from '@material-ui/icons/Assignment';
import DashboardIcon from '@material-ui/icons/Dashboard';
import { useServiceRegistryTranslation } from '../../i18n/ServiceRegistryLanguageProvider';
import { ServiceRegistryUserAccountSidebarItem } from './ServiceRegistryUserAccountSidebarItem';

type UserRoleResponse = {
  userID: string;
  roleID: string;
  roleName: string;
};

const useSidebarLogoStyles = makeStyles(theme => ({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: '109px',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: '8px',
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
  divider: {
    height: 1,
    width: '100%',
    background: theme.palette.divider,
    border: 'none',
    margin: theme.spacing(1.2, 0),
  },
}));

const SidebarLogo = () => {
  const { t } = useServiceRegistryTranslation();
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label={t('navigation.home')}>
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const SidebarDivider = () => {
  const classes = useSidebarLogoStyles();
  return <hr className={classes.divider} />;
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useServiceRegistryTranslation();
  const config = useApi(configApiRef);
  const baseUrl = config.getString('backend.baseUrl');
  const identityApi = useApi(identityApiRef);
  const [userRole, setUserRole] = useState<UserRoleResponse | null>(null);
  const [epDevPAppContextState] = useEpDevPAppContext();

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const profile = await identityApi.getProfileInfo();
        const { token } = await identityApi.getCredentials();
        const response: Response = await fetch(`${baseUrl}/api/users/Role`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email: profile.email }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch user role: ${response.statusText}`);
        }

        const userRoleResponse: UserRoleResponse = await response.json();
        setUserRole(userRoleResponse);
      } catch (error) {
        console.error(error);
      }
    };

    fetchUserRole();
  }, [identityApi]);

  if (!userRole) {
    // Optionally, add a loading state here
    return <Progress />;
  }

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label={t('navigation.menu')} icon={<MenuIcon />}>
          <SidebarItem icon={HomeIcon} to="/" text={t('navigation.home')} />
          <SidebarItem
            icon={SearchIcon}
            to="/search/developer-portal"
            text={t('navigation.search')}
          />
          
          <SidebarDivider />
          <SidebarItem icon={DashboardIcon} to="/dashboard" text={t('navigation.dashboard')} />
          <SidebarItem
            icon={EpDevPConsumerProductsIcon}
            to={EpDevPPluginIds.EpDevPConsumerProductsPluginId}
            text={t('navigation.products')}
          />
          <SidebarItem
            icon={EpDevPConsumerAppsIcon}
            to={EpDevPPluginIds.EpDevPConsumerAppsPluginId}
            text={t('navigation.applications')}
          />
          {userRole.roleName === 'Admin' && (
            <SidebarItem
            icon={QueueIcon}
            to="/queue-consumers"
            text={t('navigation.queues')}
            />
          )}
          <SidebarItem
            icon={ApiIcon}
            to={'/ep-devp-openapi'}
            text={t('navigation.apiDocs')}
          />
          {userRole.roleName === 'Admin' && (
            <SidebarItem icon={PeopleIcon} to={'/users-list'} text={t('navigation.users')} />
          )}
          {/* {userRole.roleName === 'Contract Authority' && (
            <>
              <SidebarItem
                icon={AssignmentIcon}
                to={'/contract/add'}
                text={'Add Contract'}
              />
            </>
          )} */}
          <SidebarItem
            icon={ImportContactsIcon}
            to={'/contract/view'}
            text={t('navigation.contracts')}
          />
        </SidebarGroup>

        {/* Notifications Group */}
        <SidebarGroup label={t('navigation.notifications')} icon={<AddAlertIcon />}>
          {userRole.roleName === 'Admin' && (
            <>
              {/* <SidebarItem
                icon={AddAlertIcon}
                to="/notifications/add"
                text="Add Notifications"
                style={{ width: '235px' }}
              /> */}
              <SidebarItem
                icon={NotificationsIcon}
                to="/notifications/view"
                text={t('navigation.notifications')}
                style={{ width: '235px' }}
              />
            </>
          )}
          {(userRole.roleName === 'Subscriber' ||
            userRole.roleName === 'Contract Authority') && (
            <SidebarItem
              icon={NotificationsIcon}
              to="/notifications/view"
              text={t('navigation.notifications')}
            />
          )}

            <SidebarItem
              icon={VisibilityIcon}
              to="/subscriptions/view"
              text={t('navigation.subscriptions')}
            />

        </SidebarGroup>

        <SidebarDivider />
        {userRole.roleName === 'Admin' && (
          <SidebarItem
            icon={EpDevPManageApps4ApprovalsIcon}
            to={`${EpDevPPluginIds.EpDevPManageAppsPluginId}`}
            text={t('navigation.approvals')}
          />
        )}
        {userRole.roleName === 'Admin'  && (
          <>
          <SidebarSpace />
          <SidebarDivider />
          </>
        )}
        
        <EpDevPHelpSidebarItem
          title={t('navigation.help')}
          basePath={EpDevPPluginIds.EpDevPPluginId}
        />
        <ServiceRegistryUserAccountSidebarItem
          title={epDevPAppContextState.organizationDisplayName}
        />
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
